import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { Button, Modal } from 'antd';
import { get } from 'lodash';

import SupportedMediaTooltip from 'containers/QuestionnairePage/NodeSettings/SupportedMediaTooltip';
import FileUpload from 'components/FileUpload/Loadable';
import {
  SUPPORTED_TYPES,
  IMAGE_FILE,
  AUDIO_FILE,
  VIDEO_FILE,
} from 'components/FileUpload/constants';

import Pficon from 'components/Pficon';
import messages from './messages';

const whiteBtnClasses = classNames(
  'background-grey-1',
  'text-weight-bold',
  'color-grey-10-hover',
  'border-radius-level-1',
  'border-grey-5',
  'border-grey-6-hover',
  'box-shadow-none',
);

function UploadModal({
  visible,
  media = {},
  hideModal,
  saveMedia,
  removeMedia,
  title,
  mask = true,
  supportedMedia = [],
}) {
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [temporaryMedia, setTemporaryMedia] = useState(media || {});

  const onAddMedia = useCallback(
    ({ url, thumbnail, type, filename, success }) => {
      if (success) {
        setTemporaryMedia({
          url,
          thumbnail,
          type,
          filename,
        });
      }
    },
    [],
  );

  const onRemoveMedia = useCallback(() => {
    setTemporaryMedia({});
  }, []);

  const onStartUpload = useCallback(() => setSubmitDisabled(true), []);
  const onCompleteUpload = useCallback(
    (payload) => {
      onAddMedia(payload);
      setSubmitDisabled(false);
    },
    [onAddMedia],
  );

  const onSave = useCallback(() => {
    const mediaUrl = get(media, 'url', '');

    if (mediaUrl !== temporaryMedia.url) {
      removeMedia(media);
    }

    saveMedia(temporaryMedia);
    hideModal();
  }, [media, temporaryMedia, removeMedia, saveMedia, hideModal]);

  useEffect(() => {
    if (!visible) {
      setSubmitDisabled(false);
      setTemporaryMedia({});
    } else {
      setTemporaryMedia(media || {});
    }
  }, [visible]);
  return (
    <Modal
      wrapClassName="upload-modal-wrap"
      className="move-questions-modal"
      visible={visible}
      onCancel={hideModal}
      mask={mask}
      title={
        Boolean(title) && (
          <h3 className="margin-none text-weight-bold">{title}</h3>
        )
      }
      footer={null}
      closeIcon={
        <Pficon
          type="close"
          className="color-grey-10 color-grey-8-hover transition-normal"
        />
      }
      destroyOnClose
    >
      <p className="text-size-secondary color-grey-10">
        <FormattedMessage {...messages.modal.labelUpload} />
      </p>

      <div className="margin-btm-lg">
        <div className="flex flex-items-center padding-horizontal-md padding-vertical-sm border-grey-5 border-radius-top-level-1 border-bottom-none">
          <SupportedMediaTooltip supportedMedia={supportedMedia}>
            {supportedMedia.includes(SUPPORTED_TYPES[IMAGE_FILE]) && (
              <Pficon
                type="ab-image"
                className="margin-right-sm color-grey-10 text-size-h3"
                title="image"
              />
            )}
            {supportedMedia.includes(SUPPORTED_TYPES[VIDEO_FILE]) && (
              <Pficon
                type="ab-video"
                className="margin-right-sm color-grey-10 text-size-h3"
                title="video"
              />
            )}
            {supportedMedia.includes(SUPPORTED_TYPES[AUDIO_FILE]) && (
              <Pficon
                type="ab-sound"
                className="color-grey-10 text-size-h3"
                title="sound"
              />
            )}
          </SupportedMediaTooltip>
        </div>

        <FileUpload
          uploadUrl="/api/v3/survey-templates/media?"
          filePath={temporaryMedia?.url || ''}
          thumbnail={temporaryMedia?.thumbnail}
          mediaType={temporaryMedia?.type}
          disableControl={temporaryMedia?.disableControl}
          onStartUpload={onStartUpload}
          onCompleteUpload={onCompleteUpload}
          onRemove={onRemoveMedia}
          size="full"
          messageClassName=""
          message={<FormattedHTMLMessage {...messages.modal.uploadMessage} />}
          supportedMedia={supportedMedia}
          doNotShowHints
        />
      </div>

      <footer className="flex">
        <div className="flex-1">
          <Button className={whiteBtnClasses} onClick={hideModal}>
            <FormattedMessage {...messages.modal.cancel} />
          </Button>
        </div>

        <Button
          disabled={submitDisabled}
          className={classNames(
            'color-grey-1 color-grey-1-hover background-blue-6-hover background-blue-6 border-blue-6 border-blue-6-hover box-shadow-none border-radius-level-1 text-weight-bold margin-left-sm',
            {
              'opacity-50': submitDisabled,
            },
          )}
          onClick={onSave}
        >
          <FormattedMessage {...messages.modal.submit} />
        </Button>
      </footer>
    </Modal>
  );
}

UploadModal.propTypes = {
  visible: PropTypes.bool,
  media: PropTypes.object,
  hideModal: PropTypes.func,
  removeMedia: PropTypes.func,
  updateAttributeValue: PropTypes.func,
  maskStyle: PropTypes.object,
  supportedMedia: PropTypes.array,
};

export default UploadModal;
